import React, { useEffect, useState } from 'react';
import { Users, TrendingUp, Image as ImageIcon, CreditCard, Activity, Sparkles, MessageSquare, Database, Server, Bell, AlertCircle } from 'lucide-react';
import { useFetch, fmtDateTime, fmtEur } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { StatCardsSkeleton, Skeleton } from '../_primitives/Skeleton';
import { ErrorState } from '../_primitives/ErrorState';
import { EmptyState } from '../_primitives/EmptyState';
import { Sparkline } from '../_primitives/Sparkline';
import { RevenueDonut } from '../RevenueDonutLazy';

/** Render a number cell — distinguishes "0" (real zero, accent color) from "—" (no data, muted). */
const NumberCell: React.FC<{ value: number | null | undefined }> = ({ value }) => {
  if (value == null) return <span style={{ color: 'var(--ink-40)' }}>—</span>;
  return <>{value.toLocaleString('nl-NL')}</>;
};

const TrendChip: React.FC<{ delta: number | null }> = ({ delta }) => {
  if (delta == null) return null;
  const dir = delta > 0.5 ? 'up' : delta < -0.5 ? 'down' : 'flat';
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
  const sign = delta > 0 ? '+' : '';
  return (
    <span className={`trend ${dir}`}>
      {arrow} {sign}{delta.toFixed(0)}%
    </span>
  );
};

/** Compute % change between last and previous period in a time-series. */
function pctDelta(series: { count: number }[] | undefined): number | null {
  if (!series || series.length < 2) return null;
  const last = series[series.length - 1].count;
  const prev = series[series.length - 2].count;
  if (prev === 0) return last > 0 ? 100 : 0;
  return ((last - prev) / prev) * 100;
}

const StatCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | null | undefined;
  trend?: number | null;
  spark?: number[];
}> = ({ icon: Icon, label, value, trend, spark }) => (
  <div className="stat-card">
    <div className="flex items-center gap-2 mb-3" style={{ color: 'var(--ink-55)' }}>
      <span style={{ color: 'var(--ink-40)' }}><Icon className="w-3.5 h-3.5" /></span>
      <p className="font-semibold uppercase tracking-[0.09em]" style={{ fontSize: '10.5px' }}>{label}</p>
    </div>
    <p
      className="num leading-none"
      style={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.03em', color: value == null ? 'var(--ink-40)' : 'var(--accent)' }}
    >
      {value == null ? '—' : value.toLocaleString('nl-NL')}
    </p>
    {trend != null && <TrendChip delta={trend} />}
    {spark && spark.length > 1 && <Sparkline data={spark} />}
  </div>
);

type HealthStatus = 'ok' | 'warn' | 'bad';
const HealthPill: React.FC<{ label: string; status: HealthStatus; meta?: string }> = ({ label, status, meta }) => (
  <span className={`health-pill ${status === 'ok' ? '' : status}`}>
    <span className="dot" />
    {label}{meta && <span style={{ opacity: 0.7, marginLeft: 4 }}>{meta}</span>}
  </span>
);

const RELATIVE = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s geleden`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} uur geleden`;
  return fmtDateTime(iso);
};

export const OverviewTab: React.FC = () => {
  const stats = useFetch(adminApi.stats);
  const revenue = useFetch(adminApi.revenue);
  const activity = useFetch(adminApi.activity);
  const feedback = useFetch(adminApi.feedback);

  // Soft auto-refresh every 60s.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (tick === 0) return;
    stats.refresh();
    activity.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const renderTrend = pctDelta(stats.data?.weeklyRenders);
  const signupTrend = pctDelta(stats.data?.weeklySignups);
  const renderSpark = stats.data?.weeklyRenders?.map((w) => w.count);
  const signupSpark = stats.data?.weeklySignups?.map((w) => w.count);

  // Health-strip — derive from existing data; mark unknown items neutrally.
  const healthItems: { label: string; status: HealthStatus; meta?: string; icon: React.ComponentType<{ size?: number }> }[] = [
    {
      label: 'Database',
      status: stats.error ? 'bad' : 'ok',
      meta: stats.error ? 'fetch-fout' : 'OK',
      icon: Database,
    },
    {
      label: 'API',
      status: stats.error || revenue.error ? 'bad' : 'ok',
      meta: stats.error || revenue.error ? 'errors' : 'OK',
      icon: Server,
    },
    {
      label: 'Trial dichtbij plan-cap',
      status: (stats.data?.usersNearLimit?.length ?? 0) > 0 ? 'warn' : 'ok',
      meta: `${stats.data?.usersNearLimit?.length ?? 0}`,
      icon: Bell,
    },
    {
      label: 'Pending betalingen',
      status: (revenue.data?.totals.pending_cents ?? 0) > 0 ? 'warn' : 'ok',
      meta: revenue.data ? fmtEur(revenue.data.totals.pending_cents) : '—',
      icon: AlertCircle,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Health strip */}
      <div className="health-strip">
        {healthItems.map((h) => <HealthPill key={h.label} label={h.label} status={h.status} meta={h.meta} />)}
        <span className="health-meta">
          <Activity size={11} />
          Live · auto-refresh elke 60s
        </span>
      </div>

      {/* Stat grid */}
      {stats.loading ? (
        <StatCardsSkeleton />
      ) : stats.error ? (
        <ErrorState message={stats.error} onRetry={stats.refresh} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Totaal gebruikers" value={stats.data?.totalUsers ?? null} trend={signupTrend} spark={signupSpark} />
          <StatCard icon={TrendingUp} label="Actief deze maand" value={stats.data?.activeUsersThisMonth ?? null} />
          <StatCard icon={ImageIcon} label="Renders MTD" value={stats.data?.rendersThisMonth ?? null} trend={renderTrend} spark={renderSpark} />
          <StatCard icon={CreditCard} label="Actieve abonnementen" value={stats.data?.activeSubscriptions ?? null} />
        </div>
      )}

      {/* Revenue donut + totals */}
      {revenue.data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <RevenueDonut planMix={revenue.data.planMix ?? []} planArpu={revenue.data.planArpu} />
          </div>
          <Section title="Totaal omzet" subtitle="Tot vandaag">
            <div className="p-5 space-y-4">
              <div>
                <p className="font-semibold uppercase tracking-[0.09em] mb-1.5" style={{ fontSize: '10.5px', color: 'var(--ink-55)' }}>Totaal</p>
                <p className="num" style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--accent)' }}>{fmtEur(revenue.data.totals.total_revenue_cents)}</p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-[0.09em] mb-1.5" style={{ fontSize: '10.5px', color: 'var(--ink-55)' }}>Openstaand</p>
                <p className="num" style={{ fontSize: '18px', fontWeight: 700, color: '#92400e' }}>{fmtEur(revenue.data.totals.pending_cents)}</p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-[0.09em] mb-1.5" style={{ fontSize: '10.5px', color: 'var(--ink-55)' }}>Aantal betalingen</p>
                <p className="num" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink)' }}>
                  <NumberCell value={revenue.data.totals.total_payments} />
                </p>
              </div>
            </div>
          </Section>
        </div>
      )}

      {/* Activity panels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title="Recente logins" subtitle="Laatste 50 gebruikers" toolbar={<Activity size={14} style={{ color: 'var(--ink-40)' }} />}>
          {activity.loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
          ) : activity.error ? (
            <ErrorState message={activity.error} onRetry={activity.refresh} />
          ) : (activity.data?.recentLogins ?? []).length === 0 ? (
            <EmptyState title="Nog geen logins" />
          ) : (
            <ul style={{ borderTop: '1px solid var(--line-soft)' }}>
              {(activity.data?.recentLogins ?? []).slice(0, 8).map((l) => (
                <li key={l.id} className="px-5 py-2.5 flex items-center justify-between gap-3" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--ink)', fontSize: '13px' }}>{l.first_name ?? l.email}</span>
                  <span style={{ color: 'var(--ink-55)', fontSize: '11px' }}>{RELATIVE(l.last_login)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recente renders" subtitle="Laatste 50 generaties" toolbar={<Sparkles size={14} style={{ color: 'var(--ink-40)' }} />}>
          {activity.loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
          ) : activity.error ? (
            <ErrorState message={activity.error} onRetry={activity.refresh} />
          ) : (activity.data?.recentRenders ?? []).length === 0 ? (
            <EmptyState title="Nog geen renders" hint="Wacht tot een gebruiker een woning aanlevert" />
          ) : (
            <ul>
              {(activity.data?.recentRenders ?? []).slice(0, 8).map((r) => (
                <li key={r.id} className="px-5 py-2.5 flex items-center justify-between gap-3" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--ink)', fontSize: '13px' }}>
                    {r.email ?? '—'} <span style={{ color: 'var(--ink-40)' }}>· {r.room_type ?? '—'}</span>
                  </span>
                  <span style={{ color: 'var(--ink-55)', fontSize: '11px' }}>{RELATIVE(r.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Recente feedback" subtitle="Wat gebruikers zeggen" toolbar={<MessageSquare size={14} style={{ color: 'var(--ink-40)' }} />}>
          {feedback.loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
          ) : feedback.error ? (
            <ErrorState message={feedback.error} onRetry={feedback.refresh} />
          ) : (feedback.data ?? []).length === 0 ? (
            <EmptyState title="Geen feedback" hint="Goed nieuws — of niemand heeft iets gezegd" />
          ) : (
            <ul>
              {(feedback.data ?? []).slice(0, 6).map((f) => (
                <li key={f.id} className="px-5 py-2.5" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <p className="line-clamp-2" style={{ color: 'var(--ink)', fontSize: '12.5px', lineHeight: 1.4 }}>{f.message}</p>
                  <p className="mt-1" style={{ color: 'var(--ink-55)', fontSize: '10.5px' }}>
                    {f.email ?? 'anoniem'} · {RELATIVE(f.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
};
